import { useState, useEffect } from 'react';

/**
 * Fetches and parses a Markdown file from /public/.
 * Entries are delimited by ## headings.
 * For news entries, an optional italicised second line (*date*) is extracted as `date`.
 *
 * Returns: { entries, loading, error }
 * Each entry: { title, date, body }
 */
function useMdEntries(filename) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.PUBLIC_URL}/${filename}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Could not load ${filename}`);
        return res.text();
      })
      .then((text) => {
        const parsed = parseMd(text);
        setEntries(parsed);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [filename]);

  return { entries, loading, error };
}

function parseMd(text) {
  // Split on ## headings (start of line)
  const chunks = text.split(/^## /m).filter((c) => c.trim() !== '');

  return chunks.map((chunk) => {
    const lines = chunk.split('\n');
    const title = lines[0].trim();

    // Check if second non-empty line is an italicised date: *...*
    let date = null;
    let bodyStartIdx = 1;
    const secondLine = lines.find((l, i) => i > 0 && l.trim() !== '');
    if (secondLine && /^\*.*\*$/.test(secondLine.trim())) {
      date = secondLine.trim().replace(/\*/g, '');
      bodyStartIdx = lines.indexOf(secondLine) + 1;
    }

    const body = lines
      .slice(bodyStartIdx)
      .join('\n')
      .trim();

    return { title, date, body };
  });
}

export default useMdEntries;
